import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import HawkamLogo from "@/components/HawkamLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-cream" data-testid="forgot-password">
      <div className="hidden md:flex flex-col justify-between bg-navy text-cream p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <HawkamLogo size={72} variant="mark" />
            <div>
              <div className="text-2xl font-serif">HAWKAM</div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold">Account Recovery</div>
            </div>
          </div>
          <h2 className="mt-16 text-4xl leading-tight">Locked out?<br />We&apos;ve got you.</h2>
          <p className="mt-6 text-cream/80 max-w-md text-sm leading-relaxed">
            Enter your admin email and we&apos;ll send you a secure one-time link to reset your password. The link expires in 1 hour and can be used only once — every request is written to the immutable audit log.
          </p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,97,0.2), transparent 40%)" }} />
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md card-elev p-10">
          <Link to="/admin/login" className="text-xs text-navy/60 hover:text-gold inline-flex items-center gap-1 mb-4">
            <ArrowLeft size={12} /> Back to sign in
          </Link>
          <span className="eyebrow">Password reset</span>
          <h1 className="mt-2 text-3xl text-navy">Forgot your password?</h1>
          <span className="divider-gold" />

          {sent ? (
            <div className="space-y-4" data-testid="forgot-sent">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm text-emerald-900">
                If an account exists for <b>{email}</b>, a reset link has been emailed. Check your inbox (and spam folder) — the link expires in 1 hour.
              </div>
              <button
                type="button"
                onClick={() => { setSent(false); setEmail(""); }}
                className="btn-outline-navy w-full"
              >
                Send another link
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="relative">
                  <Mail size={16} className="absolute top-3.5 left-3 text-navy/40" />
                  <input
                    data-testid="fp-email"
                    type="email"
                    placeholder="Your admin email"
                    className="field-box pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button
                data-testid="fp-submit"
                type="submit"
                disabled={busy}
                className="btn-gold w-full mt-6 justify-center disabled:opacity-40"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {busy ? "Sending…" : "Email me a reset link"}
              </button>
              <p className="text-xs text-navy/50 mt-3 leading-relaxed text-center">
                For security we always return the same message whether the email is on file or not.
              </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
